import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { revokeTokenAndRedirect, resetOkta, REDIRECT_STORAGE_KEY, getAppsFromAccessToken } from '../../okta/';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import NoAccess from '../../assets/images/MonitorLockImage.svg';
import ApplicationCard from '../../components/ApplicationCard';
import Loader from '@tcl-boron-prefabs/loader';
import { useOktaAuth } from '@okta/okta-react';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../../styles';

function AppSwitcher({ classes }) {
  const history = useHistory();
  const [apps, setApps] = useState([]);
  const [hasRedirect, setHasRedirect] = useState(false);
  const { authState } = useOktaAuth();
  const redirect = window.sessionStorage.getItem(REDIRECT_STORAGE_KEY);

  useEffect(() => {
    setHasRedirect(!!redirect);

    async function handleRedirectsAsync() {
      // Handle all redirect cases
      await handleRedirect();

      setApps(tempApps);
    }

    function getApps() {
      let tempApps = [];
      // Coming from navigation
      if (history?.location?.state?.apps) {
        tempApps = history.location.state.apps;
      } else {
        // Coming from SSO fromLogin flow
        if (authState.isAuthenticated) {
          tempApps = getAppsFromAccessToken(authState.accessToken);
        }
      }
      return tempApps;
    }

    async function handleRedirect() {
      // Check for redirect target, if there then validate against known apps
      if (redirect) {
        const url = `${redirect || window.location.origin}`;
        window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        await revokeTokenAndRedirect(url);

        return;
      }

      // If only one app, then redirect to it
      if (tempApps.length === 1 && !hasRedirect) {
        await revokeTokenAndRedirect(tempApps[0].url);
        return;
      }
    }

    // Check for apps, if any then display switcher
    const tempApps = getApps();

    handleRedirectsAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCardClick({ url }) {
    await revokeTokenAndRedirect(url);
  }

  const startOverButton = (
    <div
      className={cn('custom-button', classes.startOverButton)}
      data-testid="start-over-button"
      onClick={async () => await resetOkta()}
    >
      Start over
    </div>
  );

  // Loading state
  if (hasRedirect) {
    return (
      <div className={cn('app-view-container', classes.loadingContainer)} data-testid="app-switcher-loading">
        <Loader />
      </div>
    );
  }

  // Multiple apps state
  if (apps.length > 1 && !hasRedirect) {
    return (
      <div className={cn('app-view-container', classes.appDashboardContainer)}>
        <div className={cn(classes.chooseYourApplication)}>
          <h2 className={cn(typography.sectionHeader, classes.chooseYourApplicationText)}>Choose your application</h2>
          {apps.map((app) => (
            <ApplicationCard key={app.name} onClick={() => onCardClick(app)} application={app.name} />
          ))}
          <br />
          {startOverButton}
        </div>
      </div>
    );
  }

  // No access state
  if (apps.length === 0 && !hasRedirect) {
    return (
      <div className={cn('app-view-container', classes.emptyAppState)}>
        <img src={NoAccess} alt="no access" className={classes.noAccessImage} />
        <h4 className={cn(typography.H4)}>NO ACCESS</h4>
        <p className={cn(typography.Paragraph)}>
          <span className={cn(typography.SupportText)}>
            Sorry! You do not have access to this page. <br />
            Please contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            with any questions
          </span>
        </p>
        {startOverButton}
      </div>
    );
  }

  // Unknown or Passthrough state
  return null;
}

export default withStyles(styles)(AppSwitcher);
